import type { Asset } from 'contentful'
import ImageBase, { type ImageBaseProps } from '@components/image-base'
import { contentfulImageLoader } from '@lib/contentful-image-loader'
import { ensureProtocol } from '@lib/utilities/page-utilities'
import {
  aspectRatioStringToNumber,
  buildContentfulImageParams,
  buildContentfulImageUrl,
  getNativeAspectRatio,
} from '@lib/services/image-service'
import { type ContentfulImagesAPI } from '@lib/types'

export type ImageContentfulProps = {
  /** Contentful Asset object representing the image */
  image: Asset
  /** Contentful aspect ratio override (e.g., "16:9") - generally comes from Contentful  */
  aspectRatioOverride?: string
  /** Contentful focal point parameter for image cropping - generally comes from Contentful */
  focalPoint?: ContentfulImagesAPI['f']
  /** Alt text for the image */
  alt?: string
} & Omit<ImageBaseProps, 'src' | 'alt'>

/**
 * Renders an image from a Contentful Asset, supporting responsive breakpoints, aspect ratio overrides,
 * and Contentful-specific image parameters. Uses ImageBase for rendering and can provide a custom loader.
 *
 * @param {ImageContentfulProps} props - The props for the Contentful image component.
 * @returns {JSX.Element | null} The rendered image element or null.
 */
const ImageContentful = ({
  image,
  columnsMax,
  breakpointWidths,
  aspectRatioOverride,
  focalPoint,
  alt,
  ...rest
}: ImageContentfulProps): JSX.Element | null => {
  // Ensure file URL exists
  const fileUrl = image.fields.file?.url
  if (!fileUrl) return null

  // Default to 3 columns if neither breakpointWidths nor columnsMax is provided
  const effectiveColsMax = columnsMax ?? (!breakpointWidths ? 3 : undefined)
  const altText = alt ?? image.fields.title ?? ''

  // Use provided aspectRatioOverride, or extract it from the Contentful image Asset
  const aspectRatio = aspectRatioStringToNumber(aspectRatioOverride) ?? getNativeAspectRatio(image)

  // Build the Contentful image URL with required params (e.g., quality, format, focal point)
  const contentfulImageParams = buildContentfulImageParams(focalPoint)
  const src = buildContentfulImageUrl(ensureProtocol(fileUrl), focalPoint)

  // Compute width/height only if using breakpointWidths (required by Next.js when fill is false)
  let width = rest.width
  let height = rest.height
  if (breakpointWidths) {
    width = breakpointWidths.max
    height = Math.round(width / aspectRatio)
  }

  return (
    <ImageBase
      src={src}
      alt={altText}
      aspectRatio={aspectRatio}
      columnsMax={effectiveColsMax}
      breakpointWidths={breakpointWidths}
      // When using breakpointWidths, Next.js requires width and height for the image.
      // These are calculated above and only spread if breakpointWidths is provided.
      {...(breakpointWidths && { width, height })}
      // When NOT using breakpointWidths, provide a custom loader for Contentful params.
      {...(!breakpointWidths && {
        loader: (props) => contentfulImageLoader(props, contentfulImageParams),
      })}
      {...rest}
    />
  )
}

export default ImageContentful
